import { useEffect, useMemo, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const TODAY = new Date();

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const TODAY_START = startOfDay(TODAY);

const parseIso = (iso) => {
    const m = iso?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!m) return null;
    const y = Number(m[1]);
    const mo = Number(m[2]) - 1;
    const d = Number(m[3]);
    const dt = new Date(y, mo, d);
    if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
    return dt;
};

const toIso = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const toDisplay = (date) => {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${m}/${d}/${date.getFullYear()}`;
};

const parseDisplay = (raw) => {
    const m = raw.match(/^(\d{2})[/-](\d{2})[/-](\d{4})$/);
    if (!m) return null;

    const month = Number(m[1]) - 1;
    const day = Number(m[2]);
    const year = Number(m[3]);
    const dt = new Date(year, month, day);

    const valid = dt.getFullYear() === year && dt.getMonth() === month && dt.getDate() === day;
    if (!valid) return null;
    if (startOfDay(dt) > TODAY_START) return null;

    return dt;
};

/**
 * DatePicker
 * DOB input with two modes:
 *   1. Manual text input (MM/DD/YYYY)
 *   2. Calendar picker with day/month/year views
 *      - Day view (month grid)
 *      - Month selection view (all 12 months)
 *      - Year selection view (10-year interval paging)
 * Returns ISO date string YYYY-MM-DD via onChange.
 */
export default function DatePicker({ value, onChange, error }) {
    const selectedDate = value ? parseIso(value) : null;

    const [displayVal, setDisplayVal] = useState(selectedDate ? toDisplay(selectedDate) : '');
    const [showPicker, setShowPicker] = useState(false);

    const [view, setView] = useState('day'); // day | month | year
    const [visibleMonth, setVisibleMonth] = useState(selectedDate || new Date(TODAY.getFullYear() - 30, 0, 1));

    const [yearPageStart, setYearPageStart] = useState(() => {
        const baseYear = (selectedDate || TODAY).getFullYear();
        return baseYear - (baseYear % 10);
    });

    useEffect(() => {
        if (!selectedDate) return;
        setDisplayVal(toDisplay(selectedDate));
        setVisibleMonth(selectedDate);
    }, [value]); // keep in sync with external form state

    const nowYear = TODAY.getFullYear();
    const maxMonthInCurrentYear = TODAY.getMonth();

    const years = useMemo(() => Array.from({ length: 10 }, (_, i) => yearPageStart + i), [yearPageStart]);

    const canGoPrevMonth = () => true;
    const canGoNextMonth = () => {
        const y = visibleMonth.getFullYear();
        const m = visibleMonth.getMonth();
        if (y < nowYear) return true;
        return m < maxMonthInCurrentYear;
    };

    const goPrevMonth = () => {
        setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() - 1, 1));
    };

    const goNextMonth = () => {
        if (!canGoNextMonth()) return;
        setVisibleMonth((current) => new Date(current.getFullYear(), current.getMonth() + 1, 1));
    };

    const onManualInput = (e) => {
        const raw = e.target.value;
        const digits = raw.replace(/\D/g, '').slice(0, 8);
        const isDeleting = raw.length < displayVal.length;

        let formatted = digits;
        if (digits.length > 2 || (digits.length === 2 && !isDeleting)) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
        }
        if (digits.length > 4 || (digits.length === 4 && !isDeleting)) {
            formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
        }

        setDisplayVal(formatted);

        const parsed = parseDisplay(formatted);
        if (!parsed) return;

        onChange(toIso(parsed));
        setVisibleMonth(parsed);
    };

    const onManualKeyDown = (e) => {
        if (
            e.ctrlKey ||
            e.metaKey ||
            e.altKey ||
            ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)
        ) {
            return;
        }

        if (!/^\d$/.test(e.key)) {
            e.preventDefault();
        }
    };

    const onDaySelect = (date) => {
        if (!date) return;
        const day = startOfDay(date);
        if (day > TODAY_START) return;

        onChange(toIso(day));
        setDisplayVal(toDisplay(day));
        setShowPicker(false);
        setView('day');
    };

    const onSelectMonth = (monthIndex) => {
        const y = visibleMonth.getFullYear();
        const candidate = new Date(y, monthIndex, 1);

        if (candidate.getFullYear() === nowYear && monthIndex > maxMonthInCurrentYear) return;

        setVisibleMonth(candidate);
        setView('day');
    };

    const onSelectYear = (year) => {
        const currentMonth = visibleMonth.getMonth();
        const nextMonth = year === nowYear && currentMonth > maxMonthInCurrentYear
            ? maxMonthInCurrentYear
            : currentMonth;

        setVisibleMonth(new Date(year, nextMonth, 1));
        setView('month');
    };

    return (
        <div>
            <div className="flex gap-2 mb-1">
                <input
                    type="text"
                    inputMode="numeric"
                    placeholder="MM/DD/YYYY"
                    value={displayVal}
                    onChange={onManualInput}
                    onKeyDown={onManualKeyDown}
                    className="flex-1 h-10 rounded-lg px-3 text-sm outline-none"
                    style={{
                        border     : `1.5px solid ${error ? '#E24B4A' : showPicker ? '#2ECC8E' : '#c5dff0'}`,
                        background : 'white',
                        color      : '#1B2F7E'
                    }}
                    aria-label="Date of birth — type MM/DD/YYYY"
                    aria-invalid={!!error}
                />

                <button
                    type="button"
                    onClick={() => setShowPicker((s) => !s)}
                    aria-label="Open date picker"
                    aria-expanded={showPicker}
                    className="w-10 h-10 flex items-center justify-center rounded-lg border transition-colors flex-shrink-0"
                    style={{
                        background  : showPicker ? '#EBF8F3' : '#EAF4FB',
                        borderColor : showPicker ? '#2ECC8E' : '#c5dff0'
                    }}
                >
                    <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke={showPicker ? '#2ECC8E' : '#1B2F7E'} strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="4" width="18" height="18" rx="2" />
                        <path d="M16 2v4M8 2v4M3 10h18" />
                    </svg>
                </button>
            </div>

            {error && <p className="text-xs mb-2" style={{ color: '#E24B4A' }}>{error}</p>}

            {showPicker && (
                <div className="rounded-lg mb-3 p-3" style={{ border: '1px solid #c5dff0', background: 'white' }}>
                    <div className="flex items-center justify-between mb-3">
                        <button
                            type="button"
                            onClick={() => {
                                if (view === 'day') goPrevMonth();
                                if (view === 'year') setYearPageStart((v) => v - 10);
                            }}
                            className="text-lg px-2"
                            style={{ color: '#1B2F7E' }}
                            aria-label="Previous"
                            disabled={view === 'month' || (view === 'day' && !canGoPrevMonth())}
                        >
                            ‹
                        </button>

                        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: '#1B2F7E' }}>
                            {view !== 'month' && (
                                <button type="button" onClick={() => setView('month')} className="underline-offset-2 hover:underline">
                                    {MONTHS[visibleMonth.getMonth()]}
                                </button>
                            )}
                            {view !== 'year' && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        const y = visibleMonth.getFullYear();
                                        setYearPageStart(y - (y % 10));
                                        setView('year');
                                    }}
                                    className="underline-offset-2 hover:underline"
                                >
                                    {visibleMonth.getFullYear()}
                                </button>
                            )}
                            {view === 'year' && <span>{yearPageStart} - {yearPageStart + 9}</span>}
                            {view === 'month' && <span>Select month</span>}
                        </div>

                        <button
                            type="button"
                            onClick={() => {
                                if (view === 'day') goNextMonth();
                                if (view === 'year') setYearPageStart((v) => v + 10);
                            }}
                            className="text-lg px-2"
                            style={{ color: '#1B2F7E' }}
                            aria-label="Next"
                            disabled={view === 'month' || (view === 'day' && !canGoNextMonth()) || (view === 'year' && yearPageStart + 10 > nowYear)}
                        >
                            ›
                        </button>
                    </div>

                    {view === 'day' && (
                        <div style={{ '--rdp-accent-color': '#2ECC8E', '--rdp-background-color': '#EBF8F3' }}>
                            <DayPicker
                                mode="single"
                                month={visibleMonth}
                                onMonthChange={setVisibleMonth}
                                selected={selectedDate || undefined}
                                onSelect={onDaySelect}
                                disabled={{ after: TODAY_START }}
                                showOutsideDays
                                fromYear={nowYear - 120}
                                toYear={nowYear}
                            />
                        </div>
                    )}

                    {view === 'month' && (
                        <div className="grid grid-cols-3 gap-2">
                            {MONTHS.map((m, i) => {
                                const y = visibleMonth.getFullYear();
                                const isFutureMonth = y === nowYear && i > maxMonthInCurrentYear;
                                const isActive = i === visibleMonth.getMonth();
                                return (
                                    <button
                                        key={m}
                                        type="button"
                                        onClick={() => !isFutureMonth && onSelectMonth(i)}
                                        disabled={isFutureMonth}
                                        className="h-9 rounded-md text-xs border"
                                        style={{
                                            borderColor: isActive ? '#2ECC8E' : '#c5dff0',
                                            background: isActive ? '#EBF8F3' : 'white',
                                            color: isFutureMonth ? '#c5dff0' : '#1B2F7E',
                                            cursor: isFutureMonth ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {m}
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {view === 'year' && (
                        <div className="grid grid-cols-5 gap-2">
                            {years.map((y) => {
                                const disabled = y > nowYear;
                                const isActive = y === visibleMonth.getFullYear();
                                return (
                                    <button
                                        key={y}
                                        type="button"
                                        onClick={() => !disabled && onSelectYear(y)}
                                        disabled={disabled}
                                        className="h-9 rounded-md text-xs border"
                                        style={{
                                            borderColor: isActive ? '#2ECC8E' : '#c5dff0',
                                            background: isActive ? '#EBF8F3' : 'white',
                                            color: disabled ? '#c5dff0' : '#1B2F7E',
                                            cursor: disabled ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {y}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}

            {selectedDate && (
                <div className="flex items-center gap-2 rounded-lg px-3 py-2 mb-3 text-sm font-medium" style={{ background: '#EBF8F3', border: '1px solid #2ECC8E', color: '#0a5c40' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#2ECC8E" strokeWidth="2.5" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
                    {MONTHS[selectedDate.getMonth()]} {selectedDate.getDate()}, {selectedDate.getFullYear()}
                </div>
            )}
        </div>
    );
}